import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  BellRing,
  Bot,
  Building2,
  Calculator,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Eye,
  FileText,
  Gauge,
  Gavel,
  GitBranch,
  Globe,
  Inbox,
  LayoutDashboard,
  Lock,
  LogOut,
  Newspaper,
  Scale,
  ScanSearch,
  TrendingUp,
  Users,
  Warehouse,
  GitCompareArrows,
  PieChart as PieChartIcon,
  FileSignature,
  Shield,
  FolderOpen,
  Landmark,
  UserCheck,
  BarChart3,
  Database,
  DatabaseZap,
  ShieldCheck,
  Workflow,
} from 'lucide-react'
import { useMsal } from '@azure/msal-react'
import clsx from 'clsx'
import {
  WORKFLOW_ORDER,
  findWorkflowModuleByPath,
  getWorkflowMeta,
  headerMetaForPath,
} from '@/config/workflowModules'
import { devSessionAccount, isAzureAuthEnabled } from '@/auth/authMode'
import { useLocalAuth } from '@/auth/LocalAuthContext'
import { useAppStore } from '@/store/appStore'
import { useNotificationStore } from '@/store/notificationStore'
import NotificationPanel from '@/components/shared/NotificationPanel'
import LiveNotificationSync from '@/components/shared/LiveNotificationSync'
import ExportApprovalSync from '@/components/shared/ExportApprovalSync'
import AIAgentPanel from '@/components/shared/AIAgentPanel'
import { useAIAgentStore } from '@/store/aiAgentStore'
import PropertyProfileDialog from '@/components/shared/PropertyProfileDialog'
import { DealDossierHost } from '@/components/deal/DealDossier'
import GlobalSearch from '@/components/shared/GlobalSearch'
import { RouteErrorBoundary } from '@/components/shared/ErrorBoundary'
import { useDemoStore } from '@/config/demoMode'
import api from '@/services/api'
import type { PlatformModuleId } from '@/types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  LayoutDashboard,
  GitBranch,
  Inbox,
  ScanSearch,
  Calculator,
  ClipboardCheck,
  Gauge,
  Gavel,
  Warehouse,
  Scale,
  Lock,
  Eye,
  Building2,
  TrendingUp,
  Globe,
  Newspaper,
  FileText,
  Users,
  GitCompare: GitCompareArrows,
  PieChart: PieChartIcon,
  FileSignature,
  Shield,
  FolderOpen,
  Landmark,
  UserCheck,
  BarChart3,
  Database,
  DatabaseZap,
  ShieldCheck,
  Workflow,
}

type IdentityProps = {
  displayName: string
  email: string
  onLogout: () => void
  sessionLabel: string
  /** Server-resolved permissions for a session /auth/me cannot describe (SSO). */
  permissions?: Record<string, string[]>
}

/**
 * Shown when a path resolves to a module this license does not include.
 *
 * Normally unreachable — the effect above redirects to the first licensed
 * module. It renders during that tick, and stands as the terminal state for a
 * license with NO modules at all, where there is nowhere to redirect to.
 */
function ModuleNotLicensed() {
  return (
    <div className="flex h-full w-full items-center justify-center p-8">
      <div className="max-w-md rounded-lg border border-amber-400/10 bg-black/30 px-6 py-5 text-center">
        <Lock size={18} className="mx-auto text-stone-600" />
        <p className="mt-3 text-[13px] font-medium text-stone-200">Not included in this license</p>
        <p className="mt-1.5 text-[11px] leading-relaxed text-stone-500">
          This module is outside the modules provisioned for your account. Contact your
          administrator if you need it enabled.
        </p>
      </div>
    </div>
  )
}

function LayoutShell({ displayName, email, onLogout, permissions: sessionPermissions }: IdentityProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { sidebarCollapsed, toggleSidebar, dashboardsNavOpen, toggleDashboardsNav } = useAppStore()
  const { unreadCount, togglePanel } = useNotificationStore()
  const { isOpen: aiOpen, toggleOpen: toggleAI, isDocked: aiDocked } = useAIAgentStore()
  const activeMeta = headerMetaForPath(location.pathname)
  const { localUser } = useLocalAuth()

  // Reconcile the demo-data flag with the server once per session.
  const hydrateDemo = useDemoStore((s) => s.hydrateFromServer)
  useEffect(() => {
    void hydrateDemo()
  }, [hydrateDemo])

  const initials = displayName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  // Calibration accounts see only the golden-deal surface. The server returns
  // 403 for everything else regardless (app/core/environment_gate.py) — this
  // just avoids presenting doors that would slam.
  const CALIBRATION_MODULES: PlatformModuleId[] = ['golden-deal-reconciliation', 'credit-box']
  const isCalibration =
    !localUser?.is_super_admin && localUser?.environment === 'calibration'

  // Role permissions, resolved server-side by the same rbac.py the endpoints
  // enforce (delivered on /auth/me as `permissions`). A module the role cannot
  // view is a module whose pages would 403, so it does not belong in the nav.
  //
  // Absent `permissions` means an older cached profile from before this
  // shipped — show everything rather than blank the sidebar and look broken.
  // The API is still authoritative either way; this is presentation.
  const permissions = sessionPermissions ?? localUser?.permissions
  const canView = (id: PlatformModuleId) => {
    if (localUser?.is_super_admin) return true
    if (!permissions) return true
    const actions = permissions[id]
    return actions ? actions.includes('view') : true
  }

  const visible = (ids: PlatformModuleId[]) =>
    (isCalibration ? ids.filter((id) => CALIBRATION_MODULES.includes(id)) : ids).filter(canView)

  const dashboardModules = visible(
    WORKFLOW_ORDER.filter((id) => getWorkflowMeta(id).group === 'dashboards'),
  )
  const preClosingModules = visible(
    WORKFLOW_ORDER.filter((id) => getWorkflowMeta(id).group === 'pre-closing'),
  )
  const postClosingModules = visible(
    WORKFLOW_ORDER.filter((id) => getWorkflowMeta(id).group === 'post-closing'),
  )
  const toolModules = visible(WORKFLOW_ORDER.filter((id) => getWorkflowMeta(id).group === 'tools'))

  // Typing the URL must not get you past the missing nav entry. The module
  // this path belongs to (null for /settings and anything else that is not a
  // workflow module — those are never gated here).
  const routedModule = findWorkflowModuleByPath(location.pathname)
  const routedModuleDenied = routedModule !== null && !canView(routedModule)
  // Dashboards render first in the nav but come LAST here: this list only
  // feeds the denied-route bounce, and moving a board to the front of it would
  // silently relocate where every existing licensed role gets bounced to.
  const firstAvailable =
    [...preClosingModules, ...postClosingModules, ...toolModules, ...dashboardModules][0] ?? null

  // Bounce rather than render a wall: the app's index route redirects to
  // /command-center, so a license without it would otherwise land on a denial
  // screen at sign-in. Redirecting sends the holder to the first module their
  // license actually includes, which is the module they should have landed on.
  useEffect(() => {
    if (routedModuleDenied && firstAvailable) {
      navigate(getWorkflowMeta(firstAvailable).route, { replace: true })
    }
  }, [routedModuleDenied, firstAvailable, navigate])

  // The Dashboards group auto-opens when you navigate INTO one of its boards,
  // so a deep link or a role landing never lands you on a page whose nav entry
  // is folded away. It fires once per entry, not on every render: re-opening
  // whenever the group is closed would make the group impossible to collapse
  // while a board is on screen.
  const onDashboardRoute =
    routedModule !== null && getWorkflowMeta(routedModule).group === 'dashboards'
  const autoOpened = useRef(false)
  useEffect(() => {
    if (!onDashboardRoute) {
      autoOpened.current = false
      return
    }
    if (autoOpened.current) return
    autoOpened.current = true
    if (!useAppStore.getState().dashboardsNavOpen) toggleDashboardsNav()
  }, [onDashboardRoute, toggleDashboardsNav])

  // Only groups that actually have something in them. `offset` keeps the
  // running index renderNavItem expects continuous across the whole sidebar.
  const navGroups = (
    [
      { label: 'Dashboards', modules: dashboardModules, collapsible: true },
      { label: 'Pre-Closing', modules: preClosingModules, collapsible: false },
      { label: 'Post-Closing', modules: postClosingModules, collapsible: false },
      { label: 'Tools', modules: toolModules, collapsible: false },
    ] as const
  )
    .filter((group) => group.modules.length > 0)
    .map((group, _i, kept) => ({
      ...group,
      offset: kept
        .slice(0, kept.indexOf(group))
        .reduce((n, g) => n + g.modules.length, 0),
    }))

  const renderNavItem = (moduleId: PlatformModuleId, _index: number, indent = false) => {
    const meta = getWorkflowMeta(moduleId)
    const IconComponent = ICON_MAP[meta.icon] || FileText
    return (
      <NavLink
        key={moduleId}
        to={meta.route}
        // The collapsed rail renders the icon alone — without this the row has
        // no accessible name and no hover affordance telling you where it goes.
        title={meta.label}
        className={({ isActive }) =>
          clsx(
            'flex items-center gap-2.5 rounded-lg border px-2.5 py-1.5 transition-all duration-150',
            indent && !sidebarCollapsed && 'pl-4',
            isActive
              ? 'border-amber-400/25 bg-amber-400/[0.08] text-stone-100'
              : 'border-transparent text-stone-400 hover:border-amber-400/10 hover:bg-white/[0.02] hover:text-stone-200'
          )
        }
      >
        <IconComponent size={14} className="flex-shrink-0" />
        {!sidebarCollapsed && (
          <>
            <span className="flex-1 text-[12px] font-medium leading-none">{meta.label}</span>
            <span className="text-[9px] font-mono uppercase tracking-widest text-stone-600">{meta.short}</span>
          </>
        )}
      </NavLink>
    )
  }

  return (
    <div className="flex h-screen overflow-hidden bg-transparent text-stone-100">
      {/* Sidebar */}
      <aside
        className={clsx(
          'flex flex-shrink-0 flex-col border-r border-amber-400/10 bg-[#090909] transition-all duration-200',
          sidebarCollapsed ? 'w-[52px] px-1.5' : 'w-[200px] px-2.5'
        )}
        style={{ paddingTop: '10px', paddingBottom: '10px' }}
      >
        {/* Logo */}
        <div className={clsx('mb-3 border-b border-amber-400/10 pb-3', sidebarCollapsed ? 'px-0.5' : 'px-1')}>
          {sidebarCollapsed ? (
            <button onClick={toggleSidebar} className="flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-amber-400/20 to-amber-600/10 text-[10px] font-bold text-amber-200">
              <img src="/helios-icon.svg" alt="H" className="h-5 w-5" />
            </button>
          ) : (
            <div className="relative flex w-full items-center justify-center py-1">
              <img src="/helios-white-logo-3x.png" alt="Helios" className="h-[3.6rem] w-auto max-w-[180px] object-contain" />
              <button onClick={toggleSidebar} className="absolute right-0 top-1/2 -translate-y-1/2 rounded p-1 text-stone-600 hover:text-stone-300">
                <ChevronDown size={12} className="rotate-90" />
              </button>
            </div>
          )}
        </div>

        {/* Navigation Modules.
            Groups a role cannot see are dropped entirely — a heading and a
            divider with nothing under them reads as a broken sidebar, not as
            a permission boundary. Dividers go BETWEEN rendered groups, so the
            first one never leads with a rule. */}
        <nav className="flex-1 space-y-0.5 overflow-y-auto">
          {navGroups.map((group, groupIndex) => {
            // Collapsible group: the parent row IS the heading. Collapsed
            // sidebar has no room for it, so the children degrade to the same
            // icon-only rows every other group uses and the parent is dropped
            // rather than becoming an unlabelled chevron.
            if (group.collapsible && !sidebarCollapsed) {
              const panelId = `nav-group-${group.label.toLowerCase()}`
              return (
                <div key={group.label}>
                  {groupIndex > 0 && <div className="my-2 h-px bg-amber-400/10" />}
                  <button
                    type="button"
                    onClick={toggleDashboardsNav}
                    aria-expanded={dashboardsNavOpen}
                    aria-controls={panelId}
                    className={clsx(
                      'flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-1.5 transition-all duration-150',
                      onDashboardRoute && !dashboardsNavOpen
                        ? 'border-amber-400/25 bg-amber-400/[0.08] text-stone-100'
                        : 'border-transparent text-stone-400 hover:border-amber-400/10 hover:bg-white/[0.02] hover:text-stone-200',
                    )}
                  >
                    <LayoutDashboard size={14} className="flex-shrink-0" />
                    <span className="flex-1 text-left text-[12px] font-medium leading-none">
                      {group.label}
                    </span>
                    {dashboardsNavOpen ? (
                      <ChevronDown size={12} className="text-stone-600" />
                    ) : (
                      <ChevronRight size={12} className="text-stone-600" />
                    )}
                  </button>
                  {/* Rendered whether or not it is open so the button's
                      aria-controls always resolves to a real element; `hidden`
                      is what collapses it, and it also keeps the children out
                      of the accessibility tree and the tab order. */}
                  <div id={panelId} hidden={!dashboardsNavOpen} className="mt-0.5 space-y-0.5">
                    {group.modules.map((moduleId, i) =>
                      renderNavItem(moduleId, group.offset + i, true),
                    )}
                  </div>
                </div>
              )
            }

            return (
              <div key={group.label}>
                {groupIndex > 0 && <div className="my-2 h-px bg-amber-400/10" />}
                {!sidebarCollapsed && (
                  <div className="mb-1.5 px-1 text-[9px] font-mono uppercase tracking-[0.2em] text-stone-600">
                    {group.label}
                  </div>
                )}
                {group.modules.map((moduleId, i) => renderNavItem(moduleId, group.offset + i))}
              </div>
            )
          })}
        </nav>

        {/* User card */}
        <div className="mt-2 border-t border-amber-400/10 pt-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/settings')}
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border border-amber-300/20 bg-gradient-to-br from-black to-[#161616] text-[10px] font-bold text-amber-200 transition-colors hover:border-amber-300/35"
              title="User Settings"
            >
              {initials}
            </button>
            {!sidebarCollapsed && (
              <div className="min-w-0 flex-1">
                <div className="truncate text-[11px] font-medium text-stone-200">{displayName}</div>
                <div className="truncate text-[9px] text-stone-600">{email}</div>
              </div>
            )}
            <button onClick={onLogout} className="rounded p-1 text-stone-600 transition-colors hover:text-amber-300" title="Sign out">
              <LogOut size={12} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Compressed Header Strip */}
        <header className="flex h-11 flex-shrink-0 items-center gap-3 border-b border-amber-400/10 bg-[#090909] px-4">
          {/* Platform wordmark */}
          <div className="text-[13px] font-semibold tracking-wide text-stone-200">CapitalMarkets<span className="text-amber-400">OS</span></div>

          <div className="h-4 w-px bg-amber-400/10" />

          {/* Active module */}
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-semibold text-stone-100">{activeMeta.label}</span>
            <span className="rounded border border-amber-400/20 bg-amber-400/[0.06] px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest text-amber-300">
              {activeMeta.short}
            </span>
          </div>

          <div className="h-4 w-px bg-amber-400/10" />

          <span className="text-[11px] text-stone-500 hidden md:inline">{activeMeta.stage}</span>

          {/* Search */}
          <GlobalSearch />

          {/* Right controls */}
          <div className="flex items-center gap-1.5">
            <button
              data-notification-bell
              onClick={togglePanel}
              className="relative rounded-md border border-amber-400/8 bg-white/[0.02] p-1.5 text-stone-500 transition-colors hover:text-amber-300"
            >
              <BellRing size={13} />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-500 text-[8px] font-bold text-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={toggleAI}
              className={clsx(
                'rounded-md border p-1.5 transition-all duration-200',
                aiOpen
                  ? 'ai-glow-pulse border-amber-400/30 bg-amber-400/[0.08] text-amber-300'
                  : 'border-amber-400/8 bg-white/[0.02] text-stone-500 hover:text-amber-300'
              )}
              title="Helios AI Assistant"
            >
              <Bot size={13} />
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="flex h-6 w-6 items-center justify-center rounded-md border border-amber-300/15 bg-gradient-to-br from-black to-[#161616] text-[9px] font-bold text-amber-200 transition-colors hover:border-amber-300/30"
              title="User Settings"
            >
              {initials}
            </button>
          </div>
        </header>

        {/* Page content + optional docked AI */}
        <div className="flex min-h-0 flex-1">
          <main className="min-h-0 flex-1 overflow-hidden">
            <div className="h-full min-h-0 w-full overflow-y-auto">
              {routedModuleDenied ? (
                <ModuleNotLicensed />
              ) : (
                <RouteErrorBoundary resetKey={location.pathname}>
                  <Outlet />
                </RouteErrorBoundary>
              )}
            </div>
          </main>
          {aiOpen && aiDocked && <AIAgentPanel />}
        </div>
      </div>

      {/* Property Profile Dialog (curated demos) + Deal Dossier (live loans) */}
      <PropertyProfileDialog />
      <DealDossierHost />

      {/* Notification Panel */}
      <NotificationPanel />
      <LiveNotificationSync />
      <ExportApprovalSync />

      {/* AI Agent Panel (overlay mode when not docked) */}
      {aiOpen && !aiDocked && <AIAgentPanel />}
    </div>
  )
}

function AzureLayout() {
  const { instance, accounts } = useMsal()
  const account = accounts[0]
  // An Entra session has no /auth/me profile, so the sidebar had no
  // permissions and showed every module — including the ones the API refuses
  // a token with no app role (resolved as the Step-1-scoped viewer, C24).
  // Until this answers, or if it fails, the sidebar falls back to showing
  // everything; the API stays authoritative either way.
  const [permissions, setPermissions] = useState<Record<string, string[]> | undefined>(undefined)
  useEffect(() => {
    let active = true
    api
      .get<{ permissions?: Record<string, string[]> }>('/auth/permissions')
      .then((res) => {
        if (active && res?.data?.permissions) setPermissions(res.data.permissions)
      })
      .catch(() => {
        // Presentation only — see above.
      })
    return () => {
      active = false
    }
  }, [account?.username])

  return (
    <LayoutShell
      displayName={account?.name || account?.username || 'User'}
      email={account?.username || ''}
      onLogout={() => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })}
      sessionLabel="Entra Session"
      permissions={permissions}
    />
  )
}

function LocalAuthLayout() {
  const { localUser, logout } = useLocalAuth()

  return (
    <LayoutShell
      displayName={localUser?.name || 'User'}
      email={localUser?.email || ''}
      onLogout={() => { logout(); window.location.href = '/' }}
      sessionLabel="Local Session"
    />
  )
}

export default function Layout() {
  const { isLocalAuth } = useLocalAuth()

  if (isLocalAuth) {
    return <LocalAuthLayout />
  }

  if (!isAzureAuthEnabled) {
    return (
      <LayoutShell
        displayName={devSessionAccount.name}
        email={devSessionAccount.username}
        onLogout={() => {}}
        sessionLabel="Local Dev Session"
      />
    )
  }

  return <AzureLayout />
}
